/**
 * Event landing-page template — networkingNight ("Abend auf der Dachterrasse").
 *
 * P9-W4: FIRST premium 2026 template. Distinct CraftJS home-page document for
 * the `networkingNight` theme, authored against the hand-built calibre
 * reference (.claude/plans/p9-reference/networking-invite-example.html):
 * split-poster invite energy — deep-indigo poster hero with floating
 * balloon-style accents (CSS only, no photo), butter-yellow CTA, cream content
 * sections, Bricolage Grotesque display type over Hanken Grotesk body.
 *
 * The poster art + motion live in `networkingNightPreset.customCss`
 * (self-hosted @font-face, @keyframes float/fade-up, scroll-reveal via
 * `animation-timeline: view()`, everything motion-gated behind
 * `prefers-reduced-motion: no-preference`). Section DOM ids = node ids, so the
 * css targets `#nwn_*` directly.
 *
 * Convention (mirrors networking/gala siblings):
 *  - Section-level marketing copy goes through td() keys (`nwn*` prefix).
 *    Hero copy keys end in `HeroHeader` / `HeroEyebrow` (P9-W2 fact hooks).
 *  - Repetitive structured child data (agenda rows, numbers, FAQ, hosts) also
 *    goes through td() keys (W3b i18n); only fictitious host names, times'
 *    <b> markup and bare numbers stay inline.
 *  - Hero primary CTA anchors "#"+DataInput node id, so OPEN registration's
 *    strip + dead-anchor fixup applies cleanly.
 */
import { defaultRegistrationConsents, toCheckboxItems } from "@/lib/website/registrationConsents";
import {
  A,
  enc,
  pv,
  tc,
  btn,
  fi,
  acceptanceFi,
  scaffold,
  emptyFieldsGroup,
  PAD,
  DIST_CARD,
  BTN_FORM_SP,
  SEC_MARGIN,
  ACCEPTANCE_VISIBILITY_FILTER,
  type N,
  type TranslationFn,
} from "../temp";
import { EASE, decorLayer, fadeUpKeyframes, fontFaces, heroShell, motionGate, revealBlock } from "./premium-css";
import type { WebsiteThemeStylePreset } from "@/types/WebsiteTheme";
import {
  SAMPLE_SPEAKER_IMAGES,
} from "@/lib/website-builder/sampleContent";


// Reference palette (networking-invite-example.html, butter shifted per spec)
const STAGE = "#312F81"; // deep-indigo poster stage (hero)
const INDIGO = "#4F46E5"; // indigo action/accent
const INDIGO_DEEP = "#4338CA"; // hover / hyperlink
const BUTTER = "#F4C64D"; // butter-yellow accent + primary CTA
const BUTTER_DEEP = "#EFB823";
const CORAL = "#FF6B6B"; // balloon coral (customCss only)
const CREAM = "#FBFAF7"; // page background
const INK = "#161616"; // near-black headings / borders
const MUTE = "#5A5A5A"; // muted body
const ON_STAGE = "#EDECFA"; // body text on indigo
const DISPLAY = "'Bricolage Grotesque', 'Arial Black', sans-serif";
const BODY_FONT = "'Hanken Grotesk', 'Helvetica Neue', sans-serif";

const LOGO = `${A}/shared/logo.svg`;

// ── Inline child data (content via td() keys; W3b i18n) ───────────────

const numbersData = (td: TranslationFn) => [
  { num: "<b>120</b>", desc: td("nwnNum1Desc") },
  { num: "<b>2</b>", desc: td("nwnNum2Desc") },
  { num: "<b>4</b>", desc: td("nwnNum3Desc") },
  { num: "<b>1</b>", desc: td("nwnNum4Desc") },
];

const agendaData = (td: TranslationFn) => [
  { time: `<b>${td("nwnAgendaRow1Time")}</b>`, title: td("nwnAgendaRow1Title"), who: td("nwnAgendaRow1Who"), desc: td("nwnAgendaRow1Desc") },
  { time: `<b>${td("nwnAgendaRow2Time")}</b>`, title: td("nwnAgendaRow2Title"), who: td("nwnAgendaRow2Who"), desc: td("nwnAgendaRow2Desc") },
  { time: `<b>${td("nwnAgendaRow3Time")}</b>`, title: td("nwnAgendaRow3Title"), who: td("nwnAgendaRow3Who"), desc: td("nwnAgendaRow3Desc") },
  { time: `<b>${td("nwnAgendaRow4Time")}</b>`, title: td("nwnAgendaRow4Title"), who: td("nwnAgendaRow4Who"), desc: td("nwnAgendaRow4Desc") },
  { time: `<b>${td("nwnAgendaRow5Time")}</b>`, title: td("nwnAgendaRow5Title"), who: td("nwnAgendaRow5Who"), desc: td("nwnAgendaRow5Desc") },
];

const hostData = (td: TranslationFn) => [
  { name: "Mara Lindqvist", role: td("nwnHost1Role") },
  { name: "Jonas Petrov", role: td("nwnHost2Role") },
  { name: "Aylin Demir", role: td("nwnHost3Role") },
];

const faqData = (td: TranslationFn) => [
  { q: `<b>${td("nwnFaq1Question")}</b>`, a: td("nwnFaq1Answer") },
  { q: `<b>${td("nwnFaq2Question")}</b>`, a: td("nwnFaq2Answer") },
  { q: `<b>${td("nwnFaq3Question")}</b>`, a: td("nwnFaq3Answer") },
  { q: `<b>${td("nwnFaq4Question")}</b>`, a: td("nwnFaq4Answer") },
  { q: `<b>${td("nwnFaq5Question")}</b>`, a: td("nwnFaq5Answer") },
];

// ── networkingNight-specific child factories ──────────────────────────

function numbersChild(td: TranslationFn, parent: string, i: number): N {
  const d = numbersData(td)[i];
  return {
    type: { resolvedName: "NumbersChild" }, isCanvas: false,
    props: {
      isVisible: true,
      isVisibleNumber: true, textNumber: d.num, fontSizeNumber: "text-4xl", textAlignNumber: "center",
      isVisibleDescription: true, textDescription: d.desc, fontSizeDescription: "text-sm", textAlignDescription: "center",
    },
    displayName: "NumbersChild", custom: { icon: "IconNumber" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function agendaChild(td: TranslationFn, parent: string, i: number): N {
  const d = agendaData(td)[i];
  return {
    type: { resolvedName: "AgendaChild" }, isCanvas: false,
    props: {
      isVisibleText1: true, textText1: d.time, fontSizeText1: "text-sm", textAlignText1: "left",
      isVisibleText2: true, textText2: d.title, fontSizeText2: "text-sm", textAlignText2: "left",
      isVisibleText3: true, textText3: d.who, fontSizeText3: "text-sm", textAlignText3: "left",
      isVisibleText: true, textText: d.desc, fontSizeText: "text-sm", textAlignText: "left", border: true,
    },
    displayName: "AgendaChild", custom: { icon: "IconCalendar" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function hostChild(td: TranslationFn, parent: string, i: number): N {
  const d = hostData(td)[i];
  return {
    type: { resolvedName: "SpeakerChild" }, isCanvas: false,
    props: {
      isVisible: true, imageUrl: SAMPLE_SPEAKER_IMAGES[i % SAMPLE_SPEAKER_IMAGES.length], imageBottomBackground: "transparent",
      isVisibleName: true, textName: d.name, fontSizeName: "text-lg", textAlignName: "left",
      isVisibleOccupation: true, textOccupation: d.role, fontSizeOccupation: "text-sm", textAlignOccupation: "left",
      icons: [{ icon: "IconBrandLinkedin" }, { icon: "IconMail" }], isVisibleIcon: true,
    },
    displayName: "SpeakerChild", custom: { icon: "IconMicrophone" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

function faqChild(td: TranslationFn, parent: string, i: number): N {
  const d = faqData(td)[i];
  return {
    type: { resolvedName: "FaQChild" }, isCanvas: false,
    props: {
      isVisibleQuestion: true, textQuestion: d.q, fontSizeQuestion: "text-sm", textAlignQuestion: "left",
      isVisibleAnswer: true, textAnswer: d.a, fontSizeAnswer: "text-sm", textAlignAnswer: "left", border: true,
    },
    displayName: "FaQChild", custom: { icon: "IconMessageQuestion" },
    parent, hidden: false, nodes: [], linkedNodes: {},
  };
}

// ── Theme CSS (P9-W4) ─────────────────────────────────────────────────
//
// Marker comment first so a later user edit is distinguishable from the
// pristine preset. Composed from the shared premium-css builders (WS3) —
// output pinned byte-for-byte in __tests__/premiumCss.test.ts. The static
// poster gradient + balloon dots remain without motion.
const CUSTOM_CSS = `/* theme:networkingNight */
${fontFaces([
  { family: "Bricolage Grotesque", file: "bricolage-grotesque", weightRange: "200 800" },
  { family: "Hanken Grotesk", file: "hanken-grotesk" },
])}

/* Poster hero — deep-indigo stage, butter/coral glow discs in the corners */
#nwn_hero {
${heroShell}
  background-image:
    radial-gradient(circle 200px at 92% -4%, ${BUTTER} 0%, ${BUTTER} 99%, transparent 100%),
    radial-gradient(circle 170px at -4% 102%, rgba(255, 107, 107, 0.62) 0%, rgba(255, 107, 107, 0.62) 99%, transparent 100%),
    linear-gradient(160deg, #3B38A8 0%, ${STAGE} 55%, #282667 100%);
}

/* Floating balloon dots (reference balloon palette: butter + coral) */
#nwn_hero::before,
#nwn_hero::after {
${decorLayer}
  border-radius: 50% 50% 50% 50% / 46% 46% 56% 56%;
  box-shadow: inset -5px -6px 10px rgba(0, 0, 0, 0.12);
}
#nwn_hero::before {
  width: 54px;
  height: 64px;
  top: 14%;
  left: 7%;
  background:
    radial-gradient(circle at 34% 27%, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0) 46%),
    ${BUTTER};
}
#nwn_hero::after {
  width: 40px;
  height: 48px;
  top: 7%;
  left: 20%;
  background:
    radial-gradient(circle at 34% 27%, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0) 46%),
    ${CORAL};
}

${motionGate(`  @keyframes nwnFloat {
    0%, 100% { transform: translateY(0) rotate(-4deg); }
    50% { transform: translateY(-12px) rotate(4deg); }
  }
${fadeUpKeyframes("nwn", 22)}

  /* Gentle balloon float on the hero's decorative dots */
  #nwn_hero::before { animation: nwnFloat 7s ease-in-out infinite; }
  #nwn_hero::after { animation: nwnFloat 9s ease-in-out infinite; animation-delay: -3s; }

  /* Poster entrance */
  #nwn_hero { animation: nwnFadeUp 0.8s ${EASE} both; }

${revealBlock("nwn", ["numbers", "datainput", "agenda", "speaker", "text", "countdown", "faq", "map", "contact"], 35)}`)}
`;

// ── Style preset ──────────────────────────────────────────────────────

export const networkingNightPreset: WebsiteThemeStylePreset = {
  // The hero is CSS poster art (no photo); heroImage just needs a valid key.
  heroImage: `${A}/shared/hero.jpg`,
  logoSrc: LOGO,
  colorPalette: { primary: STAGE, secondary: INDIGO, background: CREAM },
  fonts: { header: DISPLAY, body: BODY_FONT },
  rounding: "rounded-xl",
  recommendedSize: "medium",
  bodyTheme: { fontColor: "#4A4A4A", fontSize: "16px", layoutOption: "full-width" },
  headingsTheme: {
    h1: { fontFamily: DISPLAY, fontSize: "40px", fontColor: INK },
    h2: { fontFamily: DISPLAY, fontSize: "24px", fontColor: INK },
  },
  buttonsTheme: {
    primary: { bgColor: BUTTER, bgHoverColor: BUTTER_DEEP, borderColor: BUTTER, borderHoverColor: BUTTER_DEEP, fontColor: INK, fontHoverColor: INK, fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-xl", hoverBorderRadius: "rounded-xl" },
    secondary: { bgColor: "transparent", bgHoverColor: INDIGO, borderColor: INDIGO, borderHoverColor: INDIGO, fontColor: INDIGO, fontHoverColor: "#FFFFFF", fontSize: "16px", fontHoverSize: "16px", borderRadius: "rounded-xl", hoverBorderRadius: "rounded-xl" },
  },
  hyperlinkTheme: { fontColor: INDIGO_DEEP, fontHoverColor: STAGE, underline: true },
  // borderWidth stays "border" (the MODERN base): the config diff only ships
  // CHANGED formInput fields, and the backend's formInputBorderWidth wire
  // pattern is ^\d+px$ — a Tailwind value like "border-2" 400s the create
  // (caught live on the W7 fast-path smoke).
  formInputTheme: { labelFontColor: INK, inputFontColor: INK, backgroundColor: "#FFFFFF", border: true, borderColor: INK, borderRadius: "rounded-xl", borderStyle: "border-solid", borderWidth: "border", borderSides: [], fontFamily: BODY_FONT, fontSize: "text-sm", errorColor: "#C8392B" },
  customCss: CUSTOM_CSS,
};

// ── Template ──────────────────────────────────────────────────────────

export const networkingNight = (td: TranslationFn): string => {
  const B = "nwn_body";

  const HERO = "nwn_hero";
  const NUMBERS = "nwn_numbers";
  const DI = "nwn_datainput";
  const AGENDA = "nwn_agenda";
  const SPEAKER = "nwn_speaker";
  const TEXT = "nwn_text";
  const COUNTDOWN = "nwn_countdown";
  const FAQ = "nwn_faq";
  const MAP = "nwn_map";
  const CONTACT = "nwn_contact";

  return enc({
    ...scaffold(
      { ab: "nwn_ab", abs: "nwn_abs", body: B, ft: "nwn_ft", fts: "nwn_fts" },
      [HERO, NUMBERS, DI, AGENDA, SPEAKER, TEXT, COUNTDOWN, FAQ, MAP, CONTACT],
      false, { background: "bg-white", padding: "p-4", borderRadius: "rounded-xl" },
    ),

    // ── Hero (deep-indigo CSS poster — no photo, oversized display type) ──
    [HERO]: {
      type: { resolvedName: "Hero" }, isCanvas: false,
      props: {
        sectionHeight: 92, contentAlignment: "center", imageLayout: "background", layoutWidth: "33%", inversed: false,
        padding: PAD, distButtons: "gap-3 wb-md:gap-4", distContentImage: "gap-6 wb-md:gap-10 wb-lg:gap-16", distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", buttonAlignment: "flex-row", isVisible: true,
        isVisibleHeadline: true, textHeadline: td("nwnHeroEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left", headlineColor: BUTTER,
        isVisibleHeader: true, textHeader: td("nwnHeroHeader"), fontSizeHeader: "text-6xl", textAlignHeader: "left", headerColor: "#FFFFFF",
        isVisibleText: true, textText: td("nwnHeroText"), fontSizeText: "text-lg", textAlignText: "left", textColor: ON_STAGE,
        secondaryButtonSize: "medium", secondaryButtonText: td("nwnHeroSecondaryButton"), isVisibleSecondaryButton: false,
        secondaryButtonBorder: true, secondaryButtonUnderline: false,
        primaryButtonSize: "medium", primaryButtonText: td("nwnHeroPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false,
        // Poster surface comes from customCss (#nwn_hero gradient); the image
        // is OFF so the Hero falls back to backgroundColor under the css.
        backgroundType: "image", isVisibleImage: false, imageRounded: false, backgroundOverlayColor: "transparent",
        imageSrc: `${A}/shared/hero.jpg`, backgroundColor: STAGE,
      },
      displayName: "Hero", custom: { icon: "IconSlideshow", preview: pv("Hero-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "nwn_hero_eb", header: "nwn_hero_hd", text: "nwn_hero_tx", buttonOne: "nwn_hero_b1", buttonTwo: "nwn_hero_b2" },
    },
    nwn_hero_eb: tc(HERO, { isVisible: true, text: td("nwnHeroEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: BUTTER, header: true }),
    nwn_hero_hd: tc(HERO, { isVisible: true, text: td("nwnHeroHeader"), fontSize: "text-6xl", textAlign: "left", displayName: "header", color: "#FFFFFF", header: true }),
    nwn_hero_tx: tc(HERO, { isVisible: true, text: td("nwnHeroText"), fontSize: "text-lg", textAlign: "left", displayName: "text", color: ON_STAGE, header: false }),
    nwn_hero_b1: btn(HERO, { type: "secondary", text: td("nwnHeroSecondaryButton"), isVisible: false, displayName: "buttonOne" }),
    nwn_hero_b2: btn(HERO, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("nwnHeroPrimaryButton"), displayName: "buttonTwo" }),

    // ── Numbers (cream fact chips: people / talks / drinks / rooftop) ──
    [NUMBERS]: {
      type: { resolvedName: "Numbers" }, isCanvas: true,
      props: {
        sectionHeight: 60, contentAlignment: "center", sectionMargin: SEC_MARGIN, gridColumns: "4",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "gap-5 wb-md:gap-6", isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("nwnNumbersHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("nwnNumbersHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("nwnNumbersText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Numbers", custom: { icon: "Icon123", preview: pv("NumbersPreview"), allowedComponents: ["NumbersChild"] },
      parent: B, hidden: false, nodes: ["nwn_num1", "nwn_num2", "nwn_num3", "nwn_num4"],
      linkedNodes: { headline: "nwn_num_eb", header: "nwn_num_hd", text: "nwn_num_tx" },
    },
    nwn_num_eb: tc(NUMBERS, { isVisible: false, text: td("nwnNumbersHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    nwn_num_hd: tc(NUMBERS, { isVisible: true, text: td("nwnNumbersHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    nwn_num_tx: tc(NUMBERS, { isVisible: true, text: td("nwnNumbersText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    nwn_num1: numbersChild(td, NUMBERS, 0),
    nwn_num2: numbersChild(td, NUMBERS, 1),
    nwn_num3: numbersChild(td, NUMBERS, 2),
    nwn_num4: numbersChild(td, NUMBERS, 3),

    // ── DataInput (the RSVP card — single step, plus-one like the reference) ──
    [DI]: {
      type: { resolvedName: "DataInput" }, isCanvas: false,
      props: {
        padding: PAD, sectionMargin: SEC_MARGIN, isVisible: true, textSpacing: DIST_CARD,
        backgroundColor: "transparent", border: false, borderWidth: "border-2", borderStyle: "border-solid",
        borderSides: ["bottom"], borderRadius: undefined,
        isVisibleHeadline: true, textHeadline: td("nwnDataHeadline"), fontSizeHeadline: "text-base", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("nwnDataHeader"), fontSizeHeader: "text-lg", textAlignHeader: "center",
        isVisibleText: true, textText: td("nwnDataText"), fontSizeText: "text-sm", textAlignText: "center",
        allowUpdate: true,
        multiStepEnabled: false, stepLabels: [], multiStepMode: "pages",
        overviewEnabled: false, stepSections: [["fields-0", "companions"]],
      },
      displayName: "DataInput", custom: { icon: "IconForms", preview: pv("DataInput-Preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "nwn_di_hl", header: "nwn_di_hd", text: "nwn_di_tx",
        fieldsGroup0: "nwn_di_f0", fieldsGroup1: "nwn_di_f1", fieldsGroup2: "nwn_di_f2",
        fieldsGroup3: "nwn_di_f3", fieldsGroup4: "nwn_di_f4",
        companionsGroup: "nwn_di_comp", overviewGroup: "nwn_di_ov", checkboxes: "nwn_di_cb",
        button: "nwn_di_btn", backButton: "nwn_di_back", nextButton: "nwn_di_next",
      },
    },
    nwn_di_hl: tc(DI, { isVisible: true, text: td("nwnDataHeadline"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "center", displayName: "headline", header: true }),
    nwn_di_hd: tc(DI, { isVisible: true, text: td("nwnDataHeader"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-lg", textAlign: "center", displayName: "header", header: true }),
    nwn_di_tx: tc(DI, { isVisible: true, text: td("nwnDataText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "center", displayName: "text", header: false }),
    nwn_di_f0: {
      type: { resolvedName: "FieldsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 6, groupIndex: 0, fieldSpacing: "medium" },
      displayName: "FieldsGroup", custom: { icon: "IconForms", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["nwn_di_acceptance", "nwn_di_first", "nwn_di_last", "nwn_di_email", "nwn_di_company", "nwn_di_note"], linkedNodes: {},
    },
    // FIRST in the group, and filter-free — the question every other
    // field in this group is gated on. See `acceptanceFi`.
    nwn_di_acceptance: acceptanceFi("nwn_di_f0", td("acceptanceState")),
    nwn_di_first: fi("nwn_di_f0", { label: td("firstName"), requiredField: true, inputFieldVariable: "firstName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    nwn_di_last: fi("nwn_di_f0", { label: td("lastName"), requiredField: true, inputFieldVariable: "lastName", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    nwn_di_email: fi("nwn_di_f0", { label: td("email"), requiredField: true, inputFieldVariable: "email", gridArea: "col-span-12", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER }),
    nwn_di_company: fi("nwn_di_f0", { label: td("nwnFieldCompany"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "company", gridArea: "col-span-12", changeable: true }),
    nwn_di_note: fi("nwn_di_f0", { label: td("nwnFieldNote"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "note", multiline: true, gridArea: "col-span-12", changeable: true }),
    nwn_di_f1: emptyFieldsGroup(DI, 1),
    nwn_di_f2: emptyFieldsGroup(DI, 2),
    nwn_di_f3: emptyFieldsGroup(DI, 3),
    nwn_di_f4: emptyFieldsGroup(DI, 4),
    nwn_di_comp: {
      type: { resolvedName: "CompanionsGroup" }, isCanvas: true,
      props: { isVisible: true, fieldCount: 1, fieldSpacing: "medium", visibilityFilter: ACCEPTANCE_VISIBILITY_FILTER },
      displayName: "CompanionsGroup", custom: { icon: "IconUsers", allowedComponents: ["FormInputChild"] },
      parent: DI, hidden: false, nodes: ["nwn_di_comp_first"], linkedNodes: {},
    },
    nwn_di_comp_first: fi("nwn_di_comp", { label: td("firstName"), requiredField: false, inputFieldVariable: "firstName", gridArea: "col-span-12", changeable: true, type: "companion" }),
    nwn_di_ov_t: tc("nwn_di_ov", { isVisible: true, text: td("overviewTitle"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-base", textAlign: "left", displayName: "overviewTitle", header: true }),
    nwn_di_ov_x: tc("nwn_di_ov", { isVisible: true, text: td("overviewText"), submittedText: "", isSubmitted: false, enableSubmittedState: true, fontSize: "text-sm", textAlign: "left", displayName: "overviewText", header: false }),
    nwn_di_ov: {
      type: { resolvedName: "OverviewGroup" }, isCanvas: true,
      props: { isVisible: false, parentNodeId: DI, stepSections: [["fields-0", "companions"]], stepLabels: [] },
      displayName: "summaryGroup", custom: { icon: "IconListDetails" },
      parent: DI, hidden: false, nodes: ["nwn_di_ov_t", "nwn_di_ov_x"], linkedNodes: {},
    },
    nwn_di_cb: {
      type: { resolvedName: "Checkboxes" }, isCanvas: false,
      props: { isVisible: true, checkboxes: toCheckboxItems(defaultRegistrationConsents(td)), displayName: "checkboxes" },
      displayName: "Checkboxes", custom: { icon: "IconCheckbox" },
      parent: DI, hidden: false, nodes: [], linkedNodes: {},
    },
    nwn_di_btn: btn(DI, { linkType: "internal", linkUrl: "", text: td("nwnDataSubmit"), submittedText: "", isSubmitted: false, enableSubmittedState: true, size: "small", widthMode: "full", buttonType: "submit", margin: BTN_FORM_SP, displayName: "button" }),
    nwn_di_back: btn(DI, { linkType: "internal", type: "secondary", linkUrl: "", text: td("back"), navigationRole: "back", size: "small", widthMode: "content", isVisible: false, displayName: "backButton" }),
    nwn_di_next: btn(DI, { linkType: "internal", linkUrl: "", text: td("next"), navigationRole: "next", size: "small", widthMode: "content", isVisible: false, displayName: "nextButton" }),

    // ── Agenda (the evening, 18:00 → 22:00) ──
    [AGENDA]: {
      type: { resolvedName: "Agenda" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("nwnAgendaHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("nwnAgendaHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("nwnAgendaText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Agenda", custom: { icon: "IconListCheck", preview: pv("AgendaPreview"), allowedComponents: ["AgendaChild"] },
      parent: B, hidden: false, nodes: ["nwn_ag1", "nwn_ag2", "nwn_ag3", "nwn_ag4", "nwn_ag5"],
      linkedNodes: { headline: "nwn_ag_eb", header: "nwn_ag_hd", text: "nwn_ag_tx" },
    },
    nwn_ag_eb: tc(AGENDA, { isVisible: false, text: td("nwnAgendaHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    nwn_ag_hd: tc(AGENDA, { isVisible: true, text: td("nwnAgendaHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    nwn_ag_tx: tc(AGENDA, { isVisible: true, text: td("nwnAgendaText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    nwn_ag1: agendaChild(td, AGENDA, 0),
    nwn_ag2: agendaChild(td, AGENDA, 1),
    nwn_ag3: agendaChild(td, AGENDA, 2),
    nwn_ag4: agendaChild(td, AGENDA, 3),
    nwn_ag5: agendaChild(td, AGENDA, 4),

    // ── Hosts (Speaker section, hidden — no portrait library) ──
    [SPEAKER]: {
      type: { resolvedName: "Speaker" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextSpeakers: "gap-8 wb-md:gap-12", distSpeakers: "gap-5 wb-md:gap-6", sectionMargin: SEC_MARGIN, isVisible: false, gridColumns: "3", layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("nwnSpeakerHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("nwnSpeakerHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("nwnSpeakerText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "Speaker", custom: { icon: "IconUserStar", preview: pv("Speaker-preview"), allowedComponents: ["SpeakerChild"] },
      parent: B, hidden: false, nodes: ["nwn_sp1", "nwn_sp2", "nwn_sp3"],
      linkedNodes: { headline: "nwn_sp_eb", header: "nwn_sp_hd", text: "nwn_sp_tx" },
    },
    nwn_sp_eb: tc(SPEAKER, { isVisible: false, text: td("nwnSpeakerHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    nwn_sp_hd: tc(SPEAKER, { isVisible: true, text: td("nwnSpeakerHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    nwn_sp_tx: tc(SPEAKER, { isVisible: true, text: td("nwnSpeakerText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    nwn_sp1: hostChild(td, SPEAKER, 0),
    nwn_sp2: hostChild(td, SPEAKER, 1),
    nwn_sp3: hostChild(td, SPEAKER, 2),

    // ── Text (the host's editorial note) ──
    [TEXT]: {
      type: { resolvedName: "TextSection" }, isCanvas: false,
      props: {
        buttonAlign: "center", padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButtons: "gap-6 wb-md:gap-8", distButtons: "gap-3 wb-md:gap-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("nwnTextHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("nwnTextHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("nwnTextBody"), fontSizeText: "text-md", textAlignText: "center",
        secondaryButtonSize: "medium", secondaryButtonText: td("nwnTextSecondaryButton"), isVisibleSecondaryButton: true,
        secondaryButtonBorder: true, secondaryButtonUnderline: false, secondaryButtonAlign: "center",
        primaryButtonSize: "medium", primaryButtonText: td("nwnTextPrimaryButton"), isVisiblePrimaryButton: true,
        primaryButtonBorder: true, primaryButtonUnderline: false, primaryButtonAlign: "center",
      },
      displayName: "Text", custom: { icon: "IconAlignJustified", preview: pv("textPreview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "nwn_tx_eb", header: "nwn_tx_hd", text: "nwn_tx_tx", buttonOne: "nwn_tx_b1", buttonTwo: "nwn_tx_b2" },
    },
    nwn_tx_eb: tc(TEXT, { isVisible: false, text: td("nwnTextHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    nwn_tx_hd: tc(TEXT, { isVisible: true, text: td("nwnTextHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    nwn_tx_tx: tc(TEXT, { isVisible: true, text: td("nwnTextBody"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    nwn_tx_b1: btn(TEXT, { type: "secondary", linkType: "internal", linkUrl: "#" + CONTACT, text: td("nwnTextSecondaryButton"), displayName: "buttonOne" }),
    nwn_tx_b2: btn(TEXT, { type: "primary", linkType: "internal", linkUrl: "#" + DI, text: td("nwnTextPrimaryButton"), displayName: "buttonTwo" }),

    // ── Countdown ──
    [COUNTDOWN]: {
      type: { resolvedName: "Countdown" }, isCanvas: false,
      props: {
        sectionHeight: 40, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distButtonCountdown: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: true, buttonAlign: "left",
        isVisibleHeadline: true, textHeadline: td("nwnCountdownEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("nwnCountdownHeader"), fontSizeHeader: "text-4xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("nwnCountdownText"), fontSizeText: "text-base", textAlignText: "left",
        buttonSize: "medium", buttonText: td("nwnCountdownButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
        isVisiblCountdown: true, countdownSizeDate: "text-5xl", countdownFontTypeDate: DISPLAY, countdownFontColorDate: INK,
        countdownSize: "text-base", countdownFontColor: MUTE, countdownBorderColor: "#DEDCF2",
      },
      displayName: "Countdown", custom: { icon: "IconAlarm", preview: pv("Countdown-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "nwn_cd_eb", header: "nwn_cd_hd", text: "nwn_cd_tx", button: "nwn_cd_btn" },
    },
    nwn_cd_eb: tc(COUNTDOWN, { isVisible: true, text: td("nwnCountdownEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: INDIGO_DEEP, header: true }),
    nwn_cd_hd: tc(COUNTDOWN, { isVisible: true, text: td("nwnCountdownHeader"), fontSize: "text-4xl", textAlign: "left", displayName: "header", color: INK, header: true }),
    nwn_cd_tx: tc(COUNTDOWN, { isVisible: true, text: td("nwnCountdownText"), fontSize: "text-base", textAlign: "left", displayName: "text", color: MUTE, header: false }),
    nwn_cd_btn: btn(COUNTDOWN, { linkType: "internal", linkUrl: "#" + DI, text: td("nwnCountdownButton"), displayName: "ButtonChild" }),

    // ── FaQ ──
    [FAQ]: {
      type: { resolvedName: "FaQ" }, isCanvas: true,
      props: {
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextEntries: "gap-8 wb-md:gap-12", distEntries: "space-y-3 wb-md:space-y-4", sectionMargin: SEC_MARGIN, isVisible: true, layoutWidth: "100%",
        isVisibleHeadline: false, textHeadline: td("nwnFaqHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "center",
        isVisibleHeader: true, textHeader: td("nwnFaqHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "center",
        isVisibleText: true, textText: td("nwnFaqText"), fontSizeText: "text-md", textAlignText: "center",
      },
      displayName: "FaQ", custom: { icon: "IconQuestionMark", preview: pv("faqPreview"), allowedComponents: ["FaQChild"] },
      parent: B, hidden: false, nodes: ["nwn_fq1", "nwn_fq2", "nwn_fq3", "nwn_fq4", "nwn_fq5"],
      linkedNodes: { headline: "nwn_fq_eb", header: "nwn_fq_hd", text: "nwn_fq_tx" },
    },
    nwn_fq_eb: tc(FAQ, { isVisible: false, text: td("nwnFaqHeader"), fontSize: "text-lg", textAlign: "center", displayName: "headline", header: true }),
    nwn_fq_hd: tc(FAQ, { isVisible: true, text: td("nwnFaqHeader"), fontSize: "text-2xl", textAlign: "center", displayName: "header", header: true }),
    nwn_fq_tx: tc(FAQ, { isVisible: true, text: td("nwnFaqText"), fontSize: "text-md", textAlign: "center", displayName: "text", header: false }),
    nwn_fq1: faqChild(td, FAQ, 0),
    nwn_fq2: faqChild(td, FAQ, 1),
    nwn_fq3: faqChild(td, FAQ, 2),
    nwn_fq4: faqChild(td, FAQ, 3),
    nwn_fq5: faqChild(td, FAQ, 4),

    // ── Map (rooftop venue) — present but hidden by default ──
    [MAP]: {
      type: { resolvedName: "Map" }, isCanvas: false,
      props: {
        sectionHeight: 70, contentAlignment: "center", sectionMargin: SEC_MARGIN, layoutWidth: "50%",
        padding: PAD, distHeadlineHeader: "mb-3 wb-md:mb-4", distHeaderText: "mb-4 wb-md:mb-6", distTextButton: "mb-6 wb-md:mb-8", distContentMap: "gap-6 wb-md:gap-10 wb-lg:gap-16", isVisible: false,
        isVisibleHeadline: false, textHeadline: td("nwnMapHeader"), fontSizeHeadline: "text-lg", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("nwnMapHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("nwnMapText"), fontSizeText: "text-md", textAlignText: "left",
        buttonSize: "medium", buttonText: td("nwnMapButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false, buttonAlign: "left",
        mapRounded: "rounded-xl",
        mapZoom: 12, mapType: "m", aspectRatio: "aspect-[4/3]", isVisibleMap: true, mapLocation: td("nwnMapLocation"),
      },
      displayName: "Map", custom: { icon: "IconMap2", preview: pv("Map-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: { headline: "nwn_mp_eb", header: "nwn_mp_hd", text: "nwn_mp_tx", button: "nwn_mp_btn" },
    },
    nwn_mp_eb: tc(MAP, { isVisible: false, text: td("nwnMapHeader"), fontSize: "text-lg", textAlign: "left", displayName: "headline", header: true }),
    nwn_mp_hd: tc(MAP, { isVisible: true, text: td("nwnMapHeader"), fontSize: "text-2xl", textAlign: "left", displayName: "header", header: true }),
    nwn_mp_tx: tc(MAP, { isVisible: true, text: td("nwnMapText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    nwn_mp_btn: btn(MAP, { text: td("nwnMapButton"), displayName: "button" }),

    // ── Contact ──
    [CONTACT]: {
      type: { resolvedName: "Contact" }, isCanvas: true,
      props: {
        sectionHeight: 70, contentAlignment: "center", infoSection: true, backgroundColor: "transparent",
        padding: PAD, sectionMargin: SEC_MARGIN, textSpacing: "space-y-3 wb-md:space-y-5 wb-lg:space-y-6", isVisible: true,
        infoWidth: "50%", inversed: false,
        isVisibleHeadline: true, textHeadline: td("nwnContactEyebrow"), fontSizeHeadline: "text-xs", textAlignHeadline: "left",
        isVisibleHeader: true, textHeader: td("nwnContactHeader"), fontSizeHeader: "text-2xl", textAlignHeader: "left",
        isVisibleText: true, textText: td("nwnContactText"), fontSizeText: "text-md", textAlignText: "left",
        isSocialVisible: true, socialIcons: [{ platform: "linkedin", url: "https://linkedin.com" }, { platform: "instagram", url: "https://instagram.com" }], socialSize: 24, socialPosition: "left",
        buttonSize: "medium", buttonText: td("nwnContactButton"), isVisibleButton: true, buttonBorder: true, buttonUnderline: false,
      },
      displayName: "Contact", custom: { icon: "IconForms", preview: pv("Contact-preview") },
      parent: B, hidden: false, nodes: [],
      linkedNodes: {
        headline: "nwn_ct_eb", header: "nwn_ct_hd", text: "nwn_ct_tx",
        firstName: "nwn_ct_first", lastName: "nwn_ct_last", eMail: "nwn_ct_email",
        phoneNumber: "nwn_ct_phone", Message: "nwn_ct_msg", button: "nwn_ct_btn",
      },
    },
    nwn_ct_eb: tc(CONTACT, { isVisible: true, text: td("nwnContactEyebrow"), fontSize: "text-xs", textAlign: "left", displayName: "headline", color: INDIGO_DEEP, header: true }),
    nwn_ct_hd: tc(CONTACT, { isVisible: true, text: td("nwnContactHeader"), fontSize: "text-2xl", textAlign: "left", displayName: "header", header: true }),
    nwn_ct_tx: tc(CONTACT, { isVisible: true, text: td("nwnContactText"), fontSize: "text-md", textAlign: "left", displayName: "text", header: false }),
    nwn_ct_first: fi(CONTACT, { label: td("firstName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "firstName", changeable: false }),
    nwn_ct_last: fi(CONTACT, { label: td("lastName"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "lastName", changeable: false }),
    nwn_ct_email: fi(CONTACT, { label: td("email"), overwriteType: "EMAIL", requiredField: true, inputFieldVariable: "email", changeable: false, locked: true }),
    nwn_ct_phone: fi(CONTACT, { label: td("phoneNumber"), overwriteType: "STRING", requiredField: false, inputFieldVariable: "phone", changeable: false }),
    nwn_ct_msg: fi(CONTACT, { label: td("messageLabel"), overwriteType: "STRING", requiredField: true, inputFieldVariable: "message", multiline: true, gridArea: "col-span-12", changeable: false, locked: true }),
    nwn_ct_btn: btn(CONTACT, { linkType: "internal", linkUrl: "", text: td("nwnContactButton"), widthMode: "full", buttonType: "submit", displayName: "button", fixedNavigation: false }),
  });
};

// ── Meta sections (AppBar + Footer) ───────────────────────────────────

/** Cream bar, ink rule, logo + anchor nav + butter "RSVP" CTA. */
export const networkingNightAppbar = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "AppBarSection" }, isCanvas: false,
    props: {
      padding: "p-4 wb-md:p-5 wb-lg:p-6", isVisible: true,
      backgroundColor: CREAM,
      border: true, borderColor: INK, borderWidth: "border-2", borderStyle: "border-solid", borderSides: ["bottom"],
      sectionOrder: ["logo", "links", "social"],
      isLogoVisible: true, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-10", logoPosition: "left",
      isLinksVisible: true, linksPosition: "center",
      links: [
        { label: td("nwnLinkProgram"), url: "#nwn_agenda", linkType: "internal" },
        { label: td("nwnLinkFaq"), url: "#nwn_faq", linkType: "internal" },
        { label: td("nwnLinkContact"), url: "#nwn_contact", linkType: "internal" },
      ],
      linksColor: INK, linksFontSize: "text-sm", linksFontWeight: "font-semibold",
      isSocialVisible: false, socialIcons: [], socialPosition: "right", socialSize: 24,
      logoutButtonSize: "small", logoutButtonText: td("nwnHeroPrimaryButton"),
      logoutButtonBorder: true, logoutButtonBackgroundColor: BUTTER, logoutButtonColor: INK, logoutButtonBorderColor: BUTTER,
    },
    displayName: "AppBar", custom: { icon: "IconLayoutNavbar" },
    parent: "DCH9WIIT55", hidden: false, nodes: [], linkedNodes: {},
  });

/** Deep-indigo footer, butter top rule, three link groups + social + copyright. */
export const networkingNightFooter = (td: TranslationFn): string =>
  enc({
    type: { resolvedName: "FooterSection" }, isCanvas: false,
    props: {
      padding: "px-4 py-10 wb-md:px-6 wb-lg:px-8", isVisible: true,
      backgroundColor: STAGE,
      border: true, borderColor: BUTTER, borderWidth: "border-4", borderStyle: "border-solid", borderSides: ["top"],
      sectionOrder: ["links", "social"],
      isLogoVisible: false, logoSrc: LOGO, logoWidth: "w-32", logoHeight: "h-auto",
      isLinkGroupsVisible: true,
      linkGroups: [
        { title: td("nwnFooterEvening"), links: [
          { label: td("nwnLinkProgram"), url: "#nwn_agenda", linkType: "internal" },
          { label: td("nwnLinkFaq"), url: "#nwn_faq", linkType: "internal" },
          { label: td("nwnLinkRsvp"), url: "/register", linkType: "internal" },
        ] },
        // Event practicalities — Maps + add-to-calendar are EXTERNAL (open in
        // a new tab). URLs are placeholders the organiser edits per event.
        { title: td("nwnFooterEvent"), links: [
          { label: td("nwnLinkLocation"), url: "https://www.google.com/maps/search/?api=1&query=Berlin", linkType: "external" },
          { label: td("nwnLinkCalendar"), url: "https://calendar.google.com/calendar/render?action=TEMPLATE&text=Networking+Night", linkType: "external" },
          { label: td("nwnLinkContact"), url: "#nwn_contact", linkType: "internal" },
        ] },
        { title: td("nwnFooterLegal"), links: [
          { label: td("nwnLinkPrivacy"), url: "/privacy", linkType: "internal" },
          { label: td("nwnLinkImprint"), url: "/imprint", linkType: "internal" },
          { label: td("nwnLinkTerms"), url: "/terms", linkType: "internal" },
        ] },
      ],
      linksTitleColor: "#FFFFFF", linksTitleFontSize: "text-sm", linksTitleFontWeight: "font-semibold",
      linksColor: "#B9B6E4", linksFontSize: "text-sm", linksFontWeight: "font-normal", linksHoverColor: "hover:font-primary",
      isSocialVisible: true,
      socialIcons: [
        { altText: "linkedin", platform: "linkedin", url: "https://linkedin.com" },
        { altText: "instagram", platform: "instagram", url: "https://instagram.com" },
      ],
      socialColor: "#B9B6E4", socialHoverColor: "hover:font-primary", socialSize: 20, socialTitle: td("followUs"),
      isCopyrightVisible: true, copyrightColor: "#8B86C9", copyrightFontSize: "text-xs", copyrightCompanyName: td("yourCompany"),
    },
    displayName: "Footer", custom: { icon: "IconMenu2" },
    parent: "xaUW2UDZT8", hidden: false, nodes: [], linkedNodes: {},
  });
